import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

/**
 * Hook helper for accessing system configuration values
 *
 * Usage:
 * ```ts
 * const { getValue, getNumber, getBoolean } = useSystemConfig()
 * const trialDays = getNumber('trial_duration_days', 7)
 * const isAIEnabled = getBoolean('enable_ai_generation', true)
 * ```
 */
export const useSystemConfig = () => {
  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['system-settings', 'config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')

      if (error) throw error

      // Convert to key-value map
      return (data || []).reduce((acc, setting) => {
        acc[setting.key] = setting.value
        return acc
      }, {} as Record<string, any>)
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - configs don't change often
    refetchOnWindowFocus: false,
    retry: 3
  })

  /**
   * Unwrap value, handling corrupted double-wrapped strings from past bug
   */
  const parseValue = (value: any) => {
    if (value === null || value === undefined) return null
    // Handle corrupted double-wrapped strings (e.g. "\"value\"")
    if (typeof value === 'string' && value.startsWith('"') && value.endsWith('"')) {
      try {
        const parsed = JSON.parse(value)
        if (typeof parsed === 'string') return parsed
      } catch { /* not valid JSON, use as-is */ }
    }
    return value
  }

  /**
   * Get a configuration value with optional default
   */
  const getValue = <T = any>(key: string, defaultValue?: T): T => {
    if (isLoading) return defaultValue as T
    const value = settings[key]
    if (value === undefined || value === null) return defaultValue as T
    return parseValue(value) as T
  }

  /**
   * Get a number configuration value
   */
  const getNumber = (key: string, defaultValue: number = 0): number => {
    const value = getValue(key, defaultValue)
    const parsed = typeof value === 'string' ? parseFloat(value) : value
    return isNaN(parsed) ? defaultValue : parsed
  }

  /**
   * Get a boolean configuration value
   */
  const getBoolean = (key: string, defaultValue: boolean = false): boolean => {
    const value = getValue(key, defaultValue)
    if (typeof value === 'boolean') return value
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true' || value === '1'
    }
    return defaultValue
  }

  /**
   * Get a string configuration value
   */
  const getString = (key: string, defaultValue: string = ''): string => {
    const value = getValue(key, defaultValue)
    return String(value)
  }

  /**
   * Get an array configuration value
   */
  const getArray = <T = any>(key: string, defaultValue: T[] = []): T[] => {
    const value = getValue(key, defaultValue)
    return Array.isArray(value) ? value : defaultValue
  }

  /**
   * Get an object configuration value
   */
  const getObject = <T = any>(key: string, defaultValue: T = {} as T): T => {
    const value = getValue(key, defaultValue)
    return typeof value === 'object' && value !== null ? value : defaultValue
  }

  /**
   * Check if a configuration exists
   */
  const hasConfig = (key: string): boolean => {
    return key in settings
  }

  return {
    settings,
    isLoading,
    getValue,
    getNumber,
    getBoolean,
    getString,
    getArray,
    getObject,
    hasConfig
  }
}

/**
 * Hook to get multiple config values at once
 */
export const useSystemConfigs = (keys: string[]) => {
  const { getValue, isLoading } = useSystemConfig()

  const values = keys.reduce((acc, key) => {
    acc[key] = getValue(key)
    return acc
  }, {} as Record<string, any>)

  return {
    values,
    isLoading
  }
}

/**
 * Hook to get all configs for a specific category
 */
export const useSystemConfigsByCategory = (category: string) => {
  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['system-settings', 'config', 'category', category],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .eq('category', category)

      if (error) throw error

      return (data || []).reduce((acc, setting) => {
        acc[setting.key] = setting.value
        return acc
      }, {} as Record<string, any>)
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false
  })

  return {
    settings,
    isLoading
  }
}
